// src/pages/EmailConfigPage.js
// Portal-wide email alert settings — currently just the "Audit Due"
// reminder, sent nightly to Access Owners once a review is actually due,
// with a repeat cadence, a cap, and an optional escalation email if it
// goes unactioned too long. Deliberately its own config, separate from
// the SaaS Applications API sync feature — this applies across all three
// resource types (SaaS/Local/Shared), not just one.
import React, { useState, useEffect } from "react";

function SectionHeader({ title, description }) {
  return (
    <div style={{ marginBottom: 18, paddingBottom: 12 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: "var(--purple-dark)" }}>{title}</div>
      {description && <div style={{ fontSize: 13, color: "var(--gray-500)", marginTop: 3 }}>{description}</div>}
    </div>
  );
}

const EMPTY = {
  enabled: false,
  fromAddress: "",
  auditDueSubject: "", auditDueBody: "",
  reminderFrequencyDays: 7, maxReminders: 3,
  escalationEnabled: false, escalationDays: 14, escalationEmail: "",
};

const PLACEHOLDERS = [
  ["{{systemName}}", "The system's name"],
  ["{{ownerName}}",  "Access owner(s), comma-separated"],
  ["{{category}}",   "The system's category"],
  ["{{dueDate}}",    "When the review was due"],
  ["{{link}}",       "Direct link to start the audit review"],
];

export default function EmailConfigPage() {
  const [cfg, setCfg] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    fetch("/api/email-config")
      .then(r => r.json())
      .then(data => setCfg({ ...EMPTY, ...data }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => { setCfg(c => ({ ...c, [k]: v })); setSaved(false); };

  const handleSave = async () => {
    setSaving(true); setError(""); setSaved(false);
    try {
      const res = await fetch("/api/email-config", {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(cfg),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save");
      setCfg({ ...EMPTY, ...data });
      setSaved(true);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null); setError("");
    try {
      const res = await fetch("/api/email-config/test", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(cfg),
      });
      const data = await res.json();
      setTestResult(data);
    } catch (err) {
      setTestResult({ ok: false, error: err.message });
    } finally { setTesting(false); }
  };

  if (loading) return <div style={{ textAlign: "center", padding: 80 }}><span className="spinner" /></div>;

  return (
    <div style={{ maxWidth: 780 }}>
      <div className="page-header">
        <div>
          <div className="page-title">Emails</div>
          <div className="page-subtitle">Automated email alerts sent by the portal — currently just audit-due reminders.</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <SectionHeader title="Email Alerts" description="Master switch for every email this portal sends. Off by default." />
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 500, cursor: "pointer", marginBottom: 16 }}>
          <input type="checkbox" checked={cfg.enabled} onChange={e => set("enabled", e.target.checked)} style={{ width: "auto" }} />
          Enable email alerts
        </label>
        <label style={{ fontSize: 13, fontWeight: 600, color: "var(--gray-700)", display: "block", marginBottom: 5 }}>Send from</label>
        <input value={cfg.fromAddress} onChange={e => set("fromAddress", e.target.value)} placeholder="governance-alerts@yourclub.com" style={{ maxWidth: 360 }} />
        <div style={{ fontSize: 12, color: "var(--gray-500)", marginTop: 4 }}>
          Must be a real mailbox in your tenant. Sent via Microsoft Graph — requires the app registration to have Mail.Send permission granted by an admin.
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <SectionHeader title="Audit Due Reminder" description="Sent to a system's Access Owner(s) once its review is actually due, on the cadence below." />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: "var(--gray-700)", display: "block", marginBottom: 5 }}>Remind every (days)</label>
            <input type="number" min={1} value={cfg.reminderFrequencyDays} onChange={e => set("reminderFrequencyDays", Number(e.target.value))} style={{ maxWidth: 140 }} />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: "var(--gray-700)", display: "block", marginBottom: 5 }}>Maximum reminders</label>
            <input type="number" min={1} value={cfg.maxReminders} onChange={e => set("maxReminders", Number(e.target.value))} style={{ maxWidth: 140 }} />
          </div>
        </div>
        <div style={{ fontSize: 12, color: "var(--gray-500)", marginTop: -10, marginBottom: 16 }}>
          e.g. 7 and 3 means: remind every 7 days, stop after 3 reminders until the audit is completed and a new one falls due.
        </div>

        <label style={{ fontSize: 13, fontWeight: 600, color: "var(--gray-700)", display: "block", marginBottom: 5 }}>Subject</label>
        <input value={cfg.auditDueSubject} onChange={e => set("auditDueSubject", e.target.value)} style={{ marginBottom: 14 }} />

        <label style={{ fontSize: 13, fontWeight: 600, color: "var(--gray-700)", display: "block", marginBottom: 5 }}>Body (HTML)</label>
        <textarea value={cfg.auditDueBody} onChange={e => set("auditDueBody", e.target.value)} rows={8}
          style={{ width: "100%", fontFamily: "monospace", fontSize: 12, resize: "vertical" }} />

        <div style={{ background: "var(--gray-50)", borderRadius: "var(--radius)", padding: "10px 14px", marginTop: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Available placeholders</div>
          {PLACEHOLDERS.map(([token, desc]) => (
            <div key={token} style={{ fontSize: 12, color: "var(--gray-600)", marginBottom: 2 }}>
              <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4 }}>{token}</code> — {desc}
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 16 }}>
          <button type="button" className="btn-secondary" style={{ fontSize: 13 }} disabled={testing} onClick={handleTest}>
            {testing ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Sending…</> : "Send test email to myself"}
          </button>
          {testResult && (
            <span style={{ fontSize: 12, color: testResult.ok ? "var(--green)" : "var(--red)" }}>
              {testResult.ok ? "✓ Sent — check your inbox" : `✕ ${testResult.error}`}
            </span>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <SectionHeader title="Escalation" description="A separate, one-time email if a review still hasn't been completed well past its due date." />
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", marginBottom: 14 }}>
          <input type="checkbox" checked={cfg.escalationEnabled} onChange={e => set("escalationEnabled", e.target.checked)} style={{ width: "auto" }} />
          Send an escalation email if a review isn't completed within a set number of days of being due
        </label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 16 }}>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: "var(--gray-700)", display: "block", marginBottom: 5 }}>Days overdue before escalating</label>
            <input type="number" min={1} value={cfg.escalationDays} onChange={e => set("escalationDays", Number(e.target.value))}
              disabled={!cfg.escalationEnabled} style={{ maxWidth: 140 }} />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: "var(--gray-700)", display: "block", marginBottom: 5 }}>Send escalation to</label>
            <input value={cfg.escalationEmail} onChange={e => set("escalationEmail", e.target.value)}
              disabled={!cfg.escalationEnabled} placeholder="it-manager@yourclub.com" />
          </div>
        </div>
        <div style={{ fontSize: 12, color: "var(--gray-500)", marginTop: 8 }}>
          Sent once per overdue cycle, regardless of the reminder cadence above — not repeated on the same schedule as the owner reminders.
        </div>
      </div>

      {error && (
        <div style={{ background: "var(--red-light)", color: "var(--red)", padding: "10px 14px", borderRadius: "var(--radius)", marginBottom: 16, fontSize: 13 }}>{error}</div>
      )}

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button type="button" className="btn-primary" disabled={saving} onClick={handleSave}>
          {saving ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Saving…</> : "Save"}
        </button>
        {saved && <span style={{ fontSize: 13, color: "var(--green)" }}>✓ Saved</span>}
      </div>
    </div>
  );
}
